<template>
  <section class="ctc-pending" aria-labelledby="ctc-pending-title">
    <!-- Collapsed by default: one strip and its disclosure. -->
    <div class="ctc-pending__strip">
      <v-icon small :color="colors.indigo" class="ctc-pending__icon">
        {{ icons.mdiRestore }}
      </v-icon>
      <div class="ctc-pending__heading">
        <h2 id="ctc-pending-title" class="ctc-pending__title">{{ title }}</h2>
        <p class="ctc-pending__text">Reprenez là où vous vous êtes arrêté.</p>
      </div>
      <rmr-action-button
        :label="expanded ? 'Masquer' : 'Afficher'"
        :icon="expanded ? icons.mdiChevronUp : icons.mdiChevronDown"
        variant="ghost"
        size="sm"
        :aria-expanded="expanded ? 'true' : 'false'"
        aria-controls="ctc-pending-list"
        class="ctc-pending__toggle"
        @click="expanded = !expanded"
      />
    </div>

    <ul v-show="expanded" id="ctc-pending-list" class="ctc-pending__list">
      <li v-for="row in rows" :key="row.id" class="ctc-pending__row">
        <strong class="ctc-pending__product" :title="row.productTitle">
          {{ row.productTitle }}
        </strong>
        <span class="ctc-pending__meta">{{ row.meta }}</span>
        <span
          class="ctc-pending__status"
          :class="`ctc-pending__status--${row.status.tone}`"
        >
          <v-icon x-small :color="row.status.color">
            {{ row.status.icon }}
          </v-icon>
          {{ row.status.label }}
        </span>
        <span class="ctc-pending__actions">
          <rmr-action-button
            label="Reprendre"
            variant="transparent"
            :color="colors.indigo"
            size="xs"
            :aria-label="`Reprendre ${row.name}`"
            :disabled="row.abandoning"
            @click="$emit('resume', row.item)"
          />
          <rmr-action-button
            label="Abandonner"
            variant="transparent"
            :color="colors.danger"
            size="xs"
            :loading="row.abandoning"
            :disabled="row.abandoning"
            :aria-label="`Abandonner ${row.name}`"
            @click="askAbandon(row.item)"
          />
        </span>
      </li>
    </ul>

    <rmr-confirm-dialog
      :value="!!confirming"
      danger
      title="Abandonner cette création ?"
      :message="confirmMessage"
      confirm-label="Abandonner"
      cancel-label="Conserver"
      @input="!$event && (confirming = null)"
      @confirm="confirmAbandon"
    />
  </section>
</template>

<script>
import { computed, ref } from "@vue/composition-api";
import {
  mdiAlertCircleOutline,
  mdiCheckCircleOutline,
  mdiChevronDown,
  mdiChevronUp,
  mdiClockOutline,
  mdiRestore,
} from "@mdi/js";

import RmrActionButton from "@/components/ui/actions/RmrActionButton.vue";
import RmrConfirmDialog from "@/components/ui/actions/RmrConfirmDialog.vue";
import {
  RMR_CA_DARK,
  RMR_DANGER,
  RMR_GREEN_DARK,
  RMR_INDIGO,
  RMR_TEXT_MUTED,
} from "@/styles/tokens";

import { pendingStatusOf, resumeDateLabel } from "../ctc-model";

// Icon shape and text carry the status; the colour only repeats it.
const STATUS_LOOKS = {
  ready: { icon: mdiCheckCircleOutline, color: RMR_GREEN_DARK },
  pending: { icon: mdiClockOutline, color: RMR_CA_DARK },
  action: { icon: mdiAlertCircleOutline, color: RMR_DANGER },
};
const ABANDONING = {
  tone: "abandoning",
  label: "Abandon en cours…",
  icon: mdiClockOutline,
  color: RMR_TEXT_MUTED,
};

export default {
  name: "CtcPendingVerifications",
  components: { RmrActionButton, RmrConfirmDialog },
  props: {
    // GET /affilizz-checks items (each with its rebuilt `product`).
    verifications: {
      type: Array,
      default: () => [],
    },
    abandoningIds: {
      type: Array,
      default: () => [],
    },
  },
  setup(props, { emit }) {
    const expanded = ref(false);
    const confirming = ref(null);

    // Same product twice stays distinguishable: site, format and time.
    const rows = computed(() =>
      props.verifications.map((item) => {
        const meta = [
          item.siteName || `Site ${item.siteId}`,
          item.widgetEnabled ? item.widgetType : "Sans widget",
          resumeDateLabel(item.createdAt),
        ]
          .filter(Boolean)
          .join(" · ");
        const abandoning = props.abandoningIds.includes(item.id);
        const status = pendingStatusOf(item.affiliation);
        return {
          id: item.id,
          item,
          productTitle: item.productTitle,
          meta,
          name: `${item.productTitle}, ${meta}`,
          abandoning,
          status: abandoning
            ? ABANDONING
            : { ...status, ...STATUS_LOOKS[status.tone] },
        };
      })
    );

    return {
      askAbandon: (item) => {
        confirming.value = item;
      },
      colors: { danger: RMR_DANGER, indigo: RMR_INDIGO },
      confirmAbandon: () => {
        if (confirming.value) emit("abandon", confirming.value);
        confirming.value = null;
      },
      confirming,
      // What cancelAffilizzCheck does, nothing more (useCtcWorkspace).
      confirmMessage: computed(() =>
        confirming.value
          ? `« ${confirming.value.productTitle} » ne pourra plus être reprise : sa vérification Affilizz est annulée et, si elle venait d’une opportunité, celle-ci redevient disponible. Les contenus déjà créés chez Affilizz y restent, sans être utilisés.`
          : ""
      ),
      expanded,
      icons: {
        mdiChevronDown,
        mdiChevronUp,
        mdiRestore,
      },
      rows,
      title: computed(() => {
        const count = props.verifications.length;
        return `${count} création${count > 1 ? "s" : ""} à reprendre`;
      }),
    };
  },
};
</script>

<style lang="scss" scoped>
// Pending work is visually distinct without competing with the active step.
.ctc-pending {
  border: 1px solid $rmr-ca-tint;
  border-radius: $rmr-radius-lg;
  background: $rmr-ca-soft;
}

.ctc-pending__strip {
  display: grid;
  grid-template-columns: 20px minmax(0, 1fr) auto;
  align-items: center;
  column-gap: 10px;
  padding: 8px 8px 8px 16px;
}

.ctc-pending__heading {
  min-width: 0;
}

.ctc-pending__title {
  margin: 0;
  color: $rmr-text-primary;
  font-size: $rmr-fs-title;
  font-weight: 700;
  line-height: 1.3;
}

.ctc-pending__text {
  margin: 0;
  color: $rmr-text-muted;
  font-size: $rmr-fs-base;
}

// Rows line up with the title text, under the strip.
.ctc-pending__list {
  margin: 0;
  padding: 0 16px 4px 46px;
  list-style: none;
}

// Desktop: three short lines, the actions centred beside them.
.ctc-pending__row {
  display: grid;
  grid-template-areas:
    "product actions"
    "meta actions"
    "status actions";
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 2px 16px;
  padding: 10px 0;
  border-top: 1px solid $rmr-border;
}

.ctc-pending__product {
  overflow: hidden;
  grid-area: product;
  color: $rmr-text-primary;
  font-size: $rmr-fs-body;
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ctc-pending__meta {
  grid-area: meta;
  color: $rmr-text-muted;
  font-size: $rmr-fs-base;
  overflow-wrap: anywhere;
}

.ctc-pending__status {
  display: inline-flex;
  min-width: 0;
  grid-area: status;
  align-items: center;
  gap: 4px;
  font-size: $rmr-fs-base;
  font-weight: 600;
}

.ctc-pending__status--ready {
  color: $rmr-green-dark;
}

.ctc-pending__status--pending {
  color: $rmr-ca-dark;
}

.ctc-pending__status--action {
  color: $rmr-danger;
}

.ctc-pending__status--abandoning {
  color: $rmr-text-muted;
}

.ctc-pending__actions {
  display: flex;
  grid-area: actions;
  align-items: center;
  gap: 4px;
}

// Visible keyboard focus on every control of the strip.
.ctc-pending ::v-deep .v-btn:focus-visible {
  outline: 2px solid $rmr-indigo;
  outline-offset: 2px;
}

// Phone: no indent; the actions share the status line, no card per entry.
@media (max-width: 599px) {
  .ctc-pending__strip {
    padding-left: 12px;
  }

  .ctc-pending__list {
    padding: 0 12px 4px;
  }

  .ctc-pending__row {
    grid-template-areas:
      "product product"
      "meta meta"
      "status actions";
    column-gap: 8px;
  }
}
</style>
